import { GiftCardDetails } from '../models/serializable/gift-card/gift-card-details.js';
import { BusinessModuleRef } from '../models/serializable/business-module-ref.js';
import { BusinessPromoCode } from '../models/serializable/discounts/business-promo-code.js';
import { Discount, DiscountSource } from '../models/serializable/discounts/discount.js';
import { EventPromoCode } from '../models/serializable/discounts/event-promo-code.js';
import { LegacyEventPromoCode } from '../models/serializable/discounts/legacy-event-promo-code.js';
import { OpenTicketPass } from '../models/serializable/discounts/open-ticket-pass.js';
import { TicketGiftCard } from '../models/serializable/discounts/ticket-gift-card.js';
import { TrackingPromoCode } from '../models/serializable/discounts/tracking-promo-code.js';
import { Price } from '../models/serializable/price.js';
import { OpenAccessPassDetails } from '../models/serializable/open-access-pass/open-access-pass-details.js';
import { FlexiblePassProtocol } from '../models/serializable/open-access-pass/flexible-pass-protocol.js';
import { GiftCard } from '../models/serializable/gift-card/gift-card.js';
import { isSameData } from '../models/dto/data-model-utils.js';
import { ArgumentException } from '../exceptions.js';

export class DiscountFactory {
  static fromFlexiblePass(pass: OpenAccessPassDetails): Discount {
    const protocol = pass.protocol;
    if(!FlexiblePassProtocol.assertType(protocol)) {
      throw new Error(`Expected {${FlexiblePassProtocol.TYPE_ID}}, got: ${protocol.$typeId}`);
    }

    const maximumUses = protocol.maxUses;
    const voucherValue = maximumUses.isFinite && maximumUses.value > 0 ?
      pass.price.scale(1.0 / maximumUses.value) :
      null;

    return Discount.percentage({
      businessRef: pass.businessRef.clone(),
      voucherId: pass.uniqueId,
      voucherValue,
      code: pass.code,
      percentage: 100,
      source: DiscountSource.OpenAccessPass,
    });
  }

  static fromOpenTicketPass(ticketPass: OpenTicketPass): Discount {
    return Discount.percentage({
      businessRef: ticketPass.businessRef.clone(),
      voucherId: ticketPass.uniqueId,
      voucherValue: ticketPass.productPrice.scale(1.0 / ticketPass.maxUses),
      code: ticketPass.code,
      percentage: 100,
      source: DiscountSource.OpenTicketPass,
    });
  }

  static fromTicketGiftCard(giftCard: TicketGiftCard, value?: Price): Discount {
    return this.fromGiftCard(giftCard, value);
  }

  static fromGiftCard(giftCard: TicketGiftCard | GiftCardDetails, value?: Price): Discount {
    return Discount.fixed({
      businessRef: giftCard.businessRef.clone(),
      voucherId: giftCard.uniqueId,
      code: giftCard.code,
      value: Price.min(
          giftCard.remainingValue,
          value ?? Price.zero(giftCard.remainingValue.currency),
      ),
      source: GiftCardDetails.assertType(giftCard) ? DiscountSource.GiftCard : DiscountSource.TicketGiftCard,
    });
  }

  static fromBusinessOrGlobalGiftCard(businessRef: BusinessModuleRef, giftCard: GiftCard, value?: Price): Discount {
    if(giftCard.businessRef != null && !isSameData(giftCard.businessRef, businessRef)) {
      throw new ArgumentException('Invalid gift card. Business ref mismatch.');
    }

    return Discount.fixed({
      businessRef: businessRef.clone(),
      voucherId: giftCard.uniqueId,
      code: giftCard.code,
      value: Price.min(
          giftCard.remainingValue,
          value ?? Price.zero(giftCard.maxValue.currency),
      ),
      source: giftCard.businessRef != null ?
        DiscountSource.GiftCard:
        DiscountSource.GlobalGiftCard,
    });
  }

  static fromBusinessPromoCode(promoCode: BusinessPromoCode): Discount {
    return Discount.promoCode({
      businessRef: promoCode.businessRef.clone(),
      voucherId: promoCode.uniqueId,
      code: promoCode.code,
      protocol: promoCode.protocol,
    });
  }

  static fromEventPromoCode(promoCode: EventPromoCode): Discount {
    return Discount.promoCode({
      businessRef: promoCode.businessRef.clone(),
      voucherId: promoCode.uniqueId,
      code: promoCode.code,
      protocol: promoCode.protocol,
    });
  }

  static fromTrackingCode(trackingCode: TrackingPromoCode): Discount {
    return Discount.tracking({
      businessRef: trackingCode.businessRef.clone(),
      voucherId: trackingCode.uniqueId,
      code: trackingCode.code,
      source: DiscountSource.PromoCode,
    });
  }

  static fromLegacyPromoCode(businessRef: BusinessModuleRef, promoCode: LegacyEventPromoCode): Discount {
    return Discount.legacy({
      businessRef: businessRef.clone(),
      voucherId: promoCode.uniqueId,
      code: promoCode.uniqueId,
      percentage: promoCode.discount,
    });
  }
}
